
import React, { useState, useEffect } from "react";
import { Lead, LeadStageType, ServiceType } from "@/context/LeadContext";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  AlertCircle, 
  Loader2, 
  CheckCircle, 
  Phone, 
  Calendar, 
  MessageSquare, 
  MapPin, 
  Clock, 
  User, 
  FileText, 
  ArrowLeft,
  Navigation,
  MessageCircle
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import ThankYouMessage from "@/components/ui-custom/ThankYouMessage";
import { Badge } from "@/components/ui/badge";
import ServiceBadge from "../ui-custom/ServiceBadge";
import LeadStatusBadge from "../ui-custom/LeadStatusBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { el } from "date-fns/locale";

interface GuestLeadViewProps {
  lead?: Lead;
  onMarkViewed?: (id: string) => void;
  customActions?: React.ReactNode;
  showThankYou?: boolean;
  onUpdateStage?: (stage: LeadStageType) => void;
}

const GuestLeadView: React.FC<GuestLeadViewProps> = ({ 
  lead: propLead,
  onMarkViewed,
  customActions,
  showThankYou = false,
  onUpdateStage
}) => {
  const { id } = useParams<{ id: string }>();
  const [lead, setLead] = useState<Lead | null>(propLead || null);
  const [loading, setLoading] = useState(!propLead);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const { toast } = useToast();
  
  useEffect(() => {
    if (propLead) {
      setLead(propLead);
      return;
    }

    const fetchLead = async () => {
      if (!id) {
        setError("ID επαφής δε βρέθηκε.");
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('leads')
          .select('*')
          .eq('id', id)
          .single();

        if (error) {
          throw error;
        }
        
        if (!data) {
          setError("Η επαφή δε βρέθηκε.");
        } else {
          const fetchedLead: Lead = {
            id: data.id,
            name: data.name,
            phone: data.phone,
            address: data.address,
            postalCode: data.postal_code,
            service: data.service as ServiceType,
            timeframe: data.timeframe,
            comments: data.comments,
            stage: (data.stage || "Νέο") as LeadStageType,
            partnerId: data.partner_id,
            partnerName: null, // We don't have this info here
            createdAt: data.created_at,
            updatedAt: data.updated_at,
            smsStatus: data.sms_sent === null ? null : (data.sms_sent ? 'sent' : 'failed'),
            viewed: data.link_opened || false,
            viewedAt: null,
            latitude: data.latitude || null,
            longitude: data.longitude || null,
          };
          
          setLead(fetchedLead);
          
          // Mark as viewed if not already
          if (!fetchedLead.viewed && onMarkViewed) {
            onMarkViewed(fetchedLead.id);
          } else if (!fetchedLead.viewed) {
            // Auto-update viewed status if we don't have an external handler
            updateViewedStatus(fetchedLead.id);
          }
        }
      } catch (err) {
        console.error("Error fetching lead:", err);
        setError("Σφάλμα κατά τη φόρτωση της επαφής.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchLead();
  }, [id, propLead, onMarkViewed]);
  
  const updateViewedStatus = async (leadId: string) => {
    try {
      await supabase
        .from('leads')
        .update({ link_opened: true })
        .eq('id', leadId);
    } catch (err) {
      console.error("Error updating viewed status:", err);
    }
  };

  const handleStatusChange = async (value: string) => {
    if (!lead) return;
    
    setUpdating(true);
    
    try {
      // Update status in Supabase directly if no external handler
      if (!onUpdateStage) {
        await updateLeadStatus(lead.id, value as LeadStageType);
        toast({
          title: "Η κατάσταση ενημερώθηκε",
          description: "Η κατάσταση της επαφής ενημερώθηκε επιτυχώς.",
        });
      } else {
        await onUpdateStage(value as LeadStageType);
        toast({
          title: "Η κατάσταση ενημερώθηκε",
          description: "Η κατάσταση της επαφής ενημερώθηκε επιτυχώς.",
        });
      }
      
      // Update local state
      setLead(prev => prev ? { ...prev, stage: value as LeadStageType } : null);
    } catch (error) {
      toast({
        title: "Σφάλμα",
        description: "Υπήρξε πρόβλημα κατά την ενημέρωση της κατάστασης.",
        variant: "destructive",
      });
      console.error("Error updating status:", error);
    } finally {
      setUpdating(false);
    }
  };
  
  const updateLeadStatus = async (leadId: string, status: LeadStageType) => {
    try {
      await supabase
        .from('leads')
        .update({ stage: status })
        .eq('id', leadId);
    } catch (err) {
      console.error("Error updating lead status:", err);
      throw err;
    }
  };

  const callLead = () => {
    if (lead?.phone) {
      window.location.href = `tel:${lead.phone}`;
    }
  };

  const openWhatsApp = () => {
    if (!lead?.phone) return;
    const cleanPhone = lead.phone.replace(/[^\d+]/g, "");
    const waLink = `https://wa.me/${cleanPhone.replace(/^\+/, "")}`;
    window.open(waLink, "_blank");
  };

  const openMaps = () => {
    if (!lead?.address) return;
    const query = encodeURIComponent(lead.address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, "_blank");
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "d MMMM yyyy, HH:mm", { locale: el });
    } catch (e) {
      return "Άγνωστη ημερομηνία";
    }
  };
  
  const getTimeframeLabel = (timeframe: string) => {
    switch (timeframe) {
      case "άμεσα": return "Άμεσα";
      case "εντός3μηνών": return "Εντός 3 μηνών";
      case "κάνωΈρευνα": return "Κάνω έρευνα αγοράς";
      default: return timeframe;
    }
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
        <p className="text-muted-foreground">Φόρτωση στοιχείων επαφής...</p>
      </div>
    );
  }
  
  if (error || !lead) {
    return (
      <Card className="max-w-2xl mx-auto my-8 border-destructive">
        <CardHeader className="bg-destructive/10">
          <CardTitle className="text-destructive flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Σφάλμα
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-muted-foreground">{error || "Η επαφή δε βρέθηκε."}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => window.location.href = "/"}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Επιστροφή στην αρχική
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto my-4 sm:my-8 px-3 sm:px-4">
      {showThankYou && (
        <div className="mb-6">
          <ThankYouMessage />
        </div>
      )}
      
      <Card className="shadow-lg border border-slate-200 overflow-hidden rounded-2xl">
        <CardHeader className="pb-4 border-b bg-gradient-to-r from-blue-50 to-white">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <User className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-xl sm:text-2xl font-bold text-gray-800">{lead.name}</CardTitle>
              </div>
              <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                Καταχωρήθηκε: {formatDate(lead.createdAt)}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ServiceBadge service={lead.service} />
              <LeadStatusBadge status={lead.stage} />
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-4 sm:p-6 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Button className="h-10" onClick={callLead}>
              <Phone className="h-4 w-4 mr-1.5" />
              Κλήση
            </Button>
            <Button className="h-10" variant="outline" onClick={openWhatsApp}>
              <MessageCircle className="h-4 w-4 mr-1.5" />
              WhatsApp
            </Button>
            <Button className="h-10" variant="outline" onClick={openMaps}>
              <Navigation className="h-4 w-4 mr-1.5" />
              Χάρτης
            </Button>
            <Button
              className="h-10"
              variant="outline"
              onClick={() => window.location.href = "/"}
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Επιστροφή
            </Button>
          </div>

          {/* Contact Details Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-4 bg-blue-50/60 p-4 rounded-xl border border-blue-100">
              <h3 className="font-semibold text-lg flex items-center gap-2 text-blue-700">
                <User className="h-4 w-4" />
                Στοιχεία Επικοινωνίας
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Τηλέφωνο</p>
                    <p className="font-medium">{lead.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Διεύθυνση</p>
                    <p>{lead.address}</p>
                    {lead.postalCode && <p className="text-sm">ΤΚ: {lead.postalCode}</p>}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4 bg-green-50/60 p-4 rounded-xl border border-green-100">
              <h3 className="font-semibold text-lg flex items-center gap-2 text-green-700">
                <FileText className="h-4 w-4" />
                Λεπτομέρειες Αιτήματος
              </h3>
              
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Χρονοδιάγραμμα</p>
                  <Badge variant={lead.timeframe === "άμεσα" ? "destructive" : "outline"} className="mt-1">
                    {getTimeframeLabel(lead.timeframe)}
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <MessageSquare className="h-4 w-4 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Κατάσταση</p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <Select 
                      value={lead.stage} 
                      onValueChange={handleStatusChange}
                      disabled={updating}
                    >
                      <SelectTrigger className="w-full sm:w-[220px] bg-white">
                        <SelectValue placeholder="Αλλαγή κατάστασης" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Νέο">Νέο</SelectItem>
                        <SelectItem value="σεΕκκρεμότητα">Σε εκκρεμότητα</SelectItem>
                        <SelectItem value="έκλεισεΗΔουλειά">Έκλεισε η δουλειά</SelectItem>
                        <SelectItem value="ήθελεΠληροφορίες">Ήθελε πληροφορίες</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Comments Section */}
          {lead.comments && (
            <>
              <Separator />
              <div className="space-y-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-gray-600" />
                  Σχόλια
                </h3>
                <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
                  {lead.comments}
                </div>
              </div>
            </>
          )}

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Γρήγορο ιστορικό</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Δημιουργία</span>
                <span className="font-medium text-slate-900">{formatDate(lead.createdAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Τελευταία ενημέρωση</span>
                <span className="font-medium text-slate-900">{formatDate(lead.updatedAt)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Προβολή link</span>
                <Badge variant={lead.viewed ? "outline" : "secondary"}>
                  {lead.viewed ? "Προβλήθηκε" : "Νέα επαφή"}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
        
        <CardFooter className="px-4 sm:px-6 py-4 bg-gray-50 border-t flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          {customActions}
          
          <div className="flex items-center gap-2">
            <Badge variant={lead.viewed ? "outline" : "secondary"} className="px-2 py-1">
              {lead.viewed ? (
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Προβλήθηκε
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Νέα επαφή
                </span>
              )}
            </Badge>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default GuestLeadView;
